import { useState } from 'react';

import type { DMConversationMessage } from '@/components/pages/conversations/SocialInbox/types';

import { SUPPORTED_ATTACHMENT_TYPES } from '@/components/pages/conversations/SocialInbox/const';

import Icon from '@/components/base/Icon';
import { MediaOverlay } from '@/components/composite/MediaOverlay';
import { ImageMedia, VideoMedia } from '@/components/pages/conversations/SocialInbox/models';

import { UnsupportedMessage } from './UnsupportedMessage';

interface MessageMediaProps {
    attachment: DMConversationMessage['attachments'][0];
    isSent?: boolean;
}

const getMediaDimensions = (type: string) => {
    switch (type) {
        case 'video':
        case 'reel':
            return 'w-64 aspect-[4/5]';
        case 'story':
            return 'w-48 aspect-[9/16]';
        case 'post':
            return 'w-64 aspect-square';
        default:
            return 'max-w-64 max-h-64';
    }
};

export const MessageMedia = ({ attachment, isSent }: MessageMediaProps) => {
    const [imgError, setImgError] = useState(false);
    const isSupported = SUPPORTED_ATTACHMENT_TYPES.includes(attachment.type);
    const isVideo = attachment.type === 'video';
    const mediaDimensions = getMediaDimensions(attachment.type);
    const media = isVideo ? new VideoMedia(attachment) : new ImageMedia(attachment);

    if (!isSupported) {
        return <UnsupportedMessage isSent={isSent} />;
    }

    return (
        <div className={`flex ${isSent ? 'justify-end' : 'justify-start'} mb-2`}>
            <div className={`relative min-h-64 overflow-hidden rounded-lg ${mediaDimensions}`}>
                <MediaOverlay media={[media]} currentIndex={0} className="h-full w-full">
                    {isVideo ? (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video
                            src={attachment.url}
                            className="h-full w-full object-cover"
                            preload="metadata"
                        />
                    ) : (
                        <div className="relative h-full w-full">
                            {imgError ? (
                                <div className="flex h-64 w-64 items-center justify-center bg-icoGray-100">
                                    <Icon icon="albums" className="text-4xl text-icoGray-500" />
                                </div>
                            ) : (
                                <img
                                    src={attachment.preview_url || attachment.url}
                                    alt=""
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, 256px"
                                    onError={() => setImgError(true)}
                                />
                            )}
                        </div>
                    )}

                    {isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center bg-icoBlack/30">
                            <Icon icon="play" className="text-3xl text-white" />
                        </div>
                    )}
                </MediaOverlay>
            </div>
        </div>
    );
};
